import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { Seo } from "@/components/Seo";
import { CTABanner } from "@/components/CTABanner";
import { RecruitmentBanner } from "@/components/RecruitmentBanner";
import { useSite, phoneHref } from "@/context/SiteContext";
import { SERVICES, COUNTY_SEO, COMPANY } from "@/lib/data";

const featured = SERVICES.filter((s) => ["nemt", "wheelchair-transportation", "ambulatory-transportation"].includes(s.slug));

const serviceBlurb = (slug, county) => ({
  "nemt": `Dependable rides to medical appointments, hospitals, dialysis, and treatment centers throughout ${county} County — coordinated with brokers, facilities, and families.`,
  "wheelchair-transportation": `Wheelchair-accessible vehicles and trained drivers serving ${county} County passengers with safe securement and door-to-door assistance.`,
  "ambulatory-transportation": `Comfortable ambulatory transportation across ${county} County for passengers who board with little or no assistance.`,
}[slug]);

export default function ServiceAreaDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const content = useSite();
  const [area, setArea] = useState(undefined);

  useEffect(() => {
    api.get(`/service-areas/${slug}`).then((r) => setArea(r.data)).catch(() => setArea(null));
  }, [slug]);

  if (area === undefined) return <div className="py-40 text-center text-slate-400">Loading…</div>;
  if (area === null) {
    return (
      <div className="py-40 text-center">
        <p className="text-slate-500">This service area could not be found.</p>
        <Link to="/service-areas" className="mt-4 inline-block font-semibold text-brand-purple hover:underline">View all service areas</Link>
      </div>
    );
  }

  const local = COUNTY_SEO[slug] || {};
  const county = area.county;
  const towns = local.towns || [];

  return (
    <>
      <Seo
        title={`Non-Emergency Medical Transportation in ${county} County, PA | CareDeserved`}
        description={`CareDeserved provides non-emergency medical, wheelchair, and ambulatory transportation in ${county} County, PA${local.seat ? ` — serving ${local.seat}${towns[0] ? `, ${towns[0]}` : ""} and surrounding communities` : ""}. Call ${content.phone}.`}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-blue noise-overlay">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Link to="/service-areas" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white">
            <Icon name="ArrowRight" size={15} weight="bold" className="rotate-180" /> All Service Areas
          </Link>
          <p className="eyebrow mt-6 text-brand-teal2">Serving {county} County, {area.state}</p>
          <h1 className="mt-3 max-w-4xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Non-Emergency Medical Transportation in {county} County, PA
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-300 md:text-lg">
            {local.intro || area.description}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button data-testid="area-hero-book" onClick={() => navigate("/book-a-ride")}
              className="h-13 rounded-full bg-brand-purple px-7 py-3 font-semibold text-white hover:bg-brand-purple2">
              Book a Ride <Icon name="ArrowRight" size={16} weight="bold" className="ml-2" />
            </Button>
            <a href={phoneHref(content.phone)}>
              <Button data-testid="area-hero-call" variant="outline" className="h-13 rounded-full border-2 border-white/40 bg-transparent px-7 py-3 font-semibold text-white hover:bg-white/10">
                <Icon name="Phone" size={16} weight="fill" className="mr-2" /> {content.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* SERVICES IN COUNTY */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="max-w-3xl font-heading text-2xl font-bold text-slate-900 sm:text-3xl">
            Transportation services in {county} County
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {featured.map((s) => (
              <Link key={s.slug} to={s.path} data-testid={`area-service-${s.slug}`}
                className="group rounded-2xl border border-slate-200 bg-brand-bg p-7 transition-all hover:-translate-y-1 hover:shadow-md">
                <span className="flex h-13 w-13 items-center justify-center rounded-2xl bg-brand-blue/5 p-3 text-brand-blue">
                  <Icon name={s.icon} size={26} weight="duotone" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{s.short} in {county} County, PA</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{serviceBlurb(s.slug, county)}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-purple">
                  Learn More <Icon name="ArrowRight" size={15} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>

          {/* Local content */}
          <div className="mt-16 grid gap-12 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-8">
              {towns.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-semibold text-slate-900">Communities we serve in {county} County</h2>
                  <p className="mt-2 text-slate-600">
                    Based around {local.seat || `${county} County`}, our team provides transportation throughout {county} County, including:
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    {[local.seat, ...towns].filter(Boolean).map((t) => (
                      <span key={t} className="rounded-full border border-slate-200 bg-brand-bg px-4 py-2 text-sm font-medium text-brand-blue">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {local.destinations && (
                <div>
                  <h2 className="font-heading text-xl font-semibold text-slate-900">Where we take {county} County passengers</h2>
                  <p className="mt-2 leading-relaxed text-slate-600">{local.destinations}</p>
                </div>
              )}
              {local.note && (
                <div className="rounded-2xl border border-brand-teal/20 bg-brand-teal/5 p-6">
                  <p className="leading-relaxed text-slate-700">{local.note}</p>
                </div>
              )}
            </div>

            <aside className="rounded-2xl border border-slate-200 bg-brand-bg p-7 lg:sticky lg:top-24 self-start">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Request a ride in {county} County</h3>
              <p className="mt-2 text-sm text-slate-600">Book online or call to confirm availability in your area.</p>
              <Button data-testid="area-book" onClick={() => navigate("/book-a-ride")}
                className="mt-5 h-12 w-full rounded-full bg-brand-purple font-semibold text-white hover:bg-brand-purple2">
                Book a Ride
              </Button>
              <a href={phoneHref(content.phone)}>
                <Button data-testid="area-call" variant="outline" className="mt-3 h-12 w-full rounded-full border-brand-blue font-semibold text-brand-blue hover:bg-brand-blue hover:text-white">
                  <Icon name="Phone" size={17} weight="fill" className="mr-2" /> {content.phone}
                </Button>
              </a>
              <p className="mt-4 rounded-xl bg-white p-3 text-xs leading-relaxed text-slate-500">
                Submitting a ride request does not guarantee transportation. A CareDeserved representative will contact you to confirm availability.
              </p>
            </aside>
          </div>
        </div>
      </section>
      <RecruitmentBanner county={county} />
      <CTABanner />
    </>
  );
}
